import React, { useMemo, useEffect } from "react";
import { Row, Col, Card, Table } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import ReactApexChart from "react-apexcharts";
import {
  useGetQARequestsByModuleQuery,
  useGetQAPendingByTeamQuery,
  useGetQAContractorsPerformanceQuery,
} from "../../../Redux/service/Consultant/ConsultantDashboard";
import Loader from "../../Helpers/Loader/Loader";
import trans from "../../../utils";
import useIsMobile from "../../../hooks/useIsMobile";

export default function QACharts({
  refreshKey,
  fromDate,
  toDate,
  type,
  projectId,
}) {
  const isMobile = useIsMobile();
  const params = useMemo(
    () => ({
      fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
      toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
      type,
      projectId,
    }),
    [fromDate, toDate, type, projectId],
  );

  const {
    data: requestsByModuleData,
    isLoading: isLoadingRequests,
    refetch: refetchRequestsByModule,
  } = useGetQARequestsByModuleQuery({ refetchOnMountOrArgChange: true });

  const {
    data: pendingByTeamData,
    isLoading: isLoadingPending,
    refetch: refetchPendingByTeam,
  } = useGetQAPendingByTeamQuery({ refetchOnMountOrArgChange: true });

  const {
    data: contractorsData,
    isLoading: isLoadingContractors,
    refetch: refetchContractors,
  } = useGetQAContractorsPerformanceQuery({ refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (refreshKey > 0) {
      refetchRequestsByModule();
      refetchPendingByTeam();
      refetchContractors();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  if (isLoadingRequests || isLoadingPending || isLoadingContractors) {
    return <Loader />;
  }

  // ✅ Process Requests by Module data - SPLIT INTO TWO CHARTS
  const modules =
    requestsByModuleData?.data?.modules || requestsByModuleData?.modules || [];

  // Separate SNAG from other modules
  const snagModule = modules.find((m) => m.type === "SNAG");
  const otherModules = modules.filter((m) => m.type !== "SNAG");

  // Check if SNAG has detailed breakdown
  const hasDetailedSnagData =
    snagModule &&
    (snagModule.fix !== undefined ||
      snagModule.rectify !== undefined ||
      snagModule.verify !== undefined ||
      snagModule.reopen !== undefined ||
      snagModule.inDispute !== undefined);

  // ========================================
  // CHART 1: IR/MIR/SUBMITTAL (Original 3-bar design)
  // ========================================
  const moduleLabels = otherModules.map((m) => m.type);
  const openData = otherModules.map((m) => m.open || 0);
  const closedData = otherModules.map((m) => m.closed || 0);
  const rejectedData = otherModules.map((m) => m.rejected || 0);

  const requestsByModuleSeries = [
    {
      name: trans("Open"),
      data: openData,
    },
    {
      name: trans("Closed"),
      data: closedData,
    },
    {
      name: trans("Rejected"),
      data: rejectedData,
    },
  ];

  const requestsByModuleOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: moduleLabels,
    },
    yaxis: {
      title: {
        text: trans("Count"),
      },
    },
    fill: {
      opacity: 1,
    },
    colors: ["#023E8A", "#0077B6", "#DC2626"],
    legend: {
      position: "top",
      horizontalAlign: "right",
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  // ========================================
  // CHART 2: SNAG WORKFLOW (7 status bars)
  // ========================================
  const snagStatusLabels = hasDetailedSnagData
    ? [
        trans("Open"),
        trans("Fix"),
        trans("Rectify"),
        trans("Verify"),
        trans("Reopen"),
        trans("In Dispute"),
        trans("Closed"),
      ]
    : [trans("Open"), trans("Closed"), trans("Rejected")];

  const snagStatusData = hasDetailedSnagData
    ? [
        snagModule.open || 0,
        snagModule.fix || 0,
        snagModule.rectify || 0,
        snagModule.verify || 0,
        snagModule.reopen || 0,
        snagModule.inDispute || 0,
        snagModule.closed || 0,
      ]
    : [
        snagModule?.open || 0,
        snagModule?.closed || 0,
        snagModule?.rejected || 0,
      ];

  const snagWorkflowSeries = [
    {
      name: trans("Count"),
      data: snagStatusData,
    },
  ];

  const snagWorkflowOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        endingShape: "rounded",
        distributed: true, // Different color for each bar
      },
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: snagStatusLabels,
    },
    yaxis: {
      title: {
        text: trans("Count"),
      },
    },
    fill: {
      opacity: 1,
    },
    colors: hasDetailedSnagData
      ? [
          "#3B82F6", // Open - Blue
          "#F59E0B", // Fix - Amber
          "#A855F7", // Rectify - Purple
          "#10B981", // Verify - Green
          "#EF4444", // Reopen - Red
          "#DC2626", // In Dispute - Dark Red
          "#059669", // Closed - Dark Green
        ]
      : [
          "#023E8A", // Open - Dark Blue
          "#0077B6", // Closed - Blue
          "#DC2626", // Rejected - Red
        ],
    legend: {
      show: false, // Hide legend since we're using distributed colors
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  // Process Pending by Team data for pie chart
  const teams =
    pendingByTeamData?.data?.teams || pendingByTeamData?.teams || [];
  const teamLabels = teams.map((t) => t.team);
  const teamPendingData = teams.map((t) => t.pending || 0);

  // Generate colors based on status
  const teamColors = teams.map((t) => {
    if (t.team === "QA Team") return "#dc2626";
    if (t.team === "Consultant") return "#0077B6";
    if (t.team === "Contractors") return "#059669";
    return "#6B7280";
  });

  const pendingByTeamOptions = {
    chart: {
      type: "pie",
      width: 400,
    },
    labels: teamLabels,
    colors: teamColors,
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    legend: {
      position: "bottom",
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  // Process Contractors Performance data
  const contractors =
    contractorsData?.data?.contractors || contractorsData?.contractors || [];

  // Add keys to contractors data for table
  const contractorsWithKeys = contractors.map((contractor, index) => ({
    ...contractor,
    key: contractor.id || contractor._id || `contractor-${index}`,
  }));

  // Icon paths for medals
  const iconBasePath = `${process.env.PUBLIC_URL}/assets/qa-dashboard-icons/`;

  // Get medal icon based on contractor rank/status
  const getMedalIcon = (index) => {
    if (index === 0) {
      return `${iconBasePath}gold.svg`;
    } else if (index === 1) {
      return `${iconBasePath}silver.svg`;
    } else if (index === 2) {
      return `${iconBasePath}bronze.svg`;
    } else {
      return ``;
    }
  };

  // Get trend arrow based on backlog trend
  const getTrendArrow = (trend) => {
    if (trend === "up" || trend === "positive" || trend > 0) {
      return (
        <ArrowUpOutlined
          style={{ color: "#059669", fontSize: "12px", marginLeft: "4px" }}
        />
      );
    } else if (trend === "down" || trend === "negative" || trend < 0) {
      return (
        <ArrowDownOutlined
          style={{ color: "#DC2626", fontSize: "12px", marginLeft: "4px" }}
        />
      );
    }
    return null;
  };

  // Get score label based on status
  const getScoreLabel = (status) => {
    if (status === "red") return trans("Failed");
    if (status === "yellow") return trans("Warning");
    if (status === "green") return trans("Success");
    return trans("Unknown");
  };

  // Get status badge style
  const getStatusBadgeStyle = (status) => {
    if (status === "red") {
      return {
        backgroundColor: "#FEE2E2",
        color: "#DC2626",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
      };
    } else if (status === "yellow") {
      return {
        backgroundColor: "#FEF3C7",
        color: "#F59E0B",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
      };
    } else if (status === "green") {
      return {
        backgroundColor: "#D1FAE5",
        color: "#059669",
        padding: "4px 12px",
        borderRadius: "12px",
        fontSize: "12px",
        fontWeight: 600,
      };
    }
    return {
      backgroundColor: "#F3F4F6",
      color: "#6B7280",
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: 600,
    };
  };

  // Define table columns for contractors with grouped headers
  const contractorColumns = [
    {
      title: trans("Contractor"),
      dataIndex: "name",
      width: 180,
      fixed: "left",
      render: (value, record, index) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={getMedalIcon(index)}
            alt="medal"
            style={{
              width: "40px",
              height: "40px",
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <span className="table-element-name ps-0" style={{ fontWeight: 500 }}>
            {value || "-"}
          </span>
        </div>
      ),
    },
    {
      title: trans("Workload Overview"),
      children: [
        {
          title: trans("IRs"),
          dataIndex: "totalIRs",
          width: 80,
          align: "center",
          render: (value) => <span>{value ?? "-"}</span>,
        },
        {
          title: trans("Snags"),
          dataIndex: "totalSnags",
          width: 80,
          align: "center",
          render: (value) => <span>{value ?? "-"}</span>,
        },
        {
          title: trans("Submittals"),
          dataIndex: "totalSubmittals",
          width: 100,
          align: "center",
          render: (value) => <span>{value ?? "-"}</span>,
        },
      ],
    },
    {
      title: trans("Backlog Health"),
      children: [
        {
          title: trans("IR Backlog"),
          dataIndex: "irBacklog",
          width: 120,
          align: "center",
          render: (value, record) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>{value ?? 0}</span>
            </div>
          ),
        },
        {
          title: trans("Snag Backlog"),
          dataIndex: "snagBacklog",
          width: 120,
          align: "center",
          render: (value, record) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>{value ?? 0}</span>
            </div>
          ),
        },
        {
          title: trans("Submittals Backlog"),
          dataIndex: "submittalBacklog",
          width: 120,
          align: "center",
          render: (value, record) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span>{value ?? 0}</span>
            </div>
          ),
        },
      ],
    },
    {
      title: trans("Severity"),
      children: [
        {
          title: trans("Critical Snags"),
          dataIndex: "criticalSnags",
          width: 120,
          align: "center",
          render: (value) => (
            <span
              style={{
                backgroundColor: "#FEE2E2",
                color: "#DC2626",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                display: "inline-block",
              }}
            >
              {value ?? 0}
            </span>
          ),
        },
      ],
    },
    {
      title: trans("Performance"),
      children: [
        {
          title: trans("Score"),
          dataIndex: "score",
          width: 100,
          align: "center",
          render: (value) => (
            <span style={{ fontWeight: 600, fontSize: "14px" }}>
              {value ?? "-"}
            </span>
          ),
        },
        {
          title: trans("Status"),
          dataIndex: "status",
          width: 120,
          align: "center",
          render: (value) => (
            <span style={getStatusBadgeStyle(value)}>
              {getScoreLabel(value)}
            </span>
          ),
        },
      ],
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      {/* IR/MIR/Submittal Modules - Bar Chart */}
      <Col xs={24} lg={8}>
        <Card
          title={trans("IR / MIR / Submittal Status")}
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            height: "100%",
          }}
        >
          {otherModules.length > 0 ? (
            <ReactApexChart
              options={requestsByModuleOptions}
              series={requestsByModuleSeries}
              type="bar"
              height={350}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              {trans("No data available")}
            </div>
          )}
        </Card>
      </Col>

      {/* SNAG Workflow Status - Bar Chart */}
      <Col xs={24} lg={8}>
        <Card
          title={trans("SNAG Workflow Status")}
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            height: "100%",
          }}
        >
          {snagModule ? (
            <ReactApexChart
              options={snagWorkflowOptions}
              series={snagWorkflowSeries}
              type="bar"
              height={350}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              {trans("No SNAG data available")}
            </div>
          )}
        </Card>
      </Col>

      {/* Pending Load by Team - Pie Chart */}
      <Col xs={24} lg={8}>
        <Card
          title={trans("Pending Load by Team")}
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            height: "100%",
          }}
        >
          {teams.length > 0 ? (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <ReactApexChart
                options={pendingByTeamOptions}
                series={teamPendingData}
                type="pie"
                width={400}
              />
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "40px" }}>
              {trans("No data available")}
            </div>
          )}
        </Card>
      </Col>

      {/* Contractors Performance Table */}
      {!isMobile && (
        <Col xs={24}>
          <Card
            title={trans("Contractors Performance")}
            style={{
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              height: "100%",
            }}
          >
            {contractors.length > 0 ? (
              <div style={{ marginTop: "16px" }}>
                <Table
                  columns={contractorColumns}
                  dataSource={contractorsWithKeys}
                  loading={isLoadingContractors}
                  pagination={false}
                  scroll={{ x: "max-content", y: 400 }}
                  bordered
                  style={{
                    background: "#F5F7FA",
                  }}
                  size="middle"
                />
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px" }}>
                {trans("No data available")}
              </div>
            )}
          </Card>
        </Col>
      )}
    </Row>
  );
}
